
import React, { useState, useEffect, useRef } from 'react';
import { Todo, Bucket } from '../types';
import { useLanguage } from '../i18n';
import { X, Calendar, Watch, Repeat, CalendarClock, CalendarDays, Hash } from 'lucide-react';

interface EditTaskModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  bucketId: string;
  buckets: Bucket[]; // All buckets for cross-linking
  initialData?: Todo;
  onClose: () => void;
  onSave: (data: Partial<Todo>) => void;
}

export const EditTaskModal: React.FC<EditTaskModalProps> = ({ isOpen, mode, bucketId, buckets, initialData, onClose, onSave }) => {
  const { t } = useLanguage();
  const [text, setText] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  const [timeConsumption, setTimeConsumption] = useState<'short' | 'medium' | 'long'>('short');
  const [recurrence, setRecurrence] = useState<'daily' | 'weekly' | 'monthly' | undefined>(undefined);
  const [scheduledDate, setScheduledDate] = useState('');
  const [contributesTo, setContributesTo] = useState<string[]>([]);
  const [targetCount, setTargetCount] = useState<number>(1);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
        if (mode === 'edit' && initialData) {
            setText(initialData.text);
            setDifficulty(initialData.difficulty || 'easy');
            setTimeConsumption(initialData.timeConsumption || 'short');
            setRecurrence(initialData.recurrence);
            setScheduledDate(initialData.scheduledDate ? new Date(initialData.scheduledDate).toISOString().split('T')[0] : '');
            setContributesTo(initialData.contributesTo || []);
            setTargetCount(initialData.targetCount || 1);
        } else {
            setText('');
            setDifficulty('easy');
            setTimeConsumption('short');
            setRecurrence(undefined);
            setScheduledDate('');
            setContributesTo([]);
            setTargetCount(1);
        }
    }
  }, [isOpen, mode, initialData]);

  // Handle auto-scroll to selected value in the vertical picker
  useEffect(() => {
      if (isOpen && recurrence && scrollRef.current) {
          const selectedEl = scrollRef.current.querySelector(`[data-value="${targetCount}"]`);
          if (selectedEl) {
              selectedEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
      }
  }, [isOpen, recurrence, targetCount]);

  if (!isOpen) return null;

  const calculatePoints = (diff: 'easy' | 'medium' | 'hard', time: 'short' | 'medium' | 'long') => {
      const diffVal = diff === 'easy' ? 1 : diff === 'medium' ? 2 : 3;
      const timeVal = time === 'short' ? 1 : time === 'medium' ? 2 : 3;
      return diffVal + timeVal;
  };

  const getRecurrenceLabel = (rec: string) => {
      switch(rec) {
          case 'daily': return t.daily;
          case 'weekly': return t.weekly;
          case 'monthly': return t.monthly;
          default: return '';
      }
  };

  const handleSave = () => {
      if (!text.trim()) return;

      const points = calculatePoints(difficulty, timeConsumption);
      
      let scheduledTimestamp: number | undefined = undefined;
      if (scheduledDate) {
          const date = new Date(scheduledDate);
          if (!isNaN(date.getTime())) {
              scheduledTimestamp = date.getTime();
          }
      }

      onSave({
          text: text.trim(),
          points,
          difficulty,
          timeConsumption,
          recurrence,
          scheduledDate: scheduledTimestamp,
          contributesTo,
          targetCount: recurrence ? Math.max(1, targetCount) : 1,
          currentCount: initialData?.currentCount || 0
      });
      onClose();
  };

  const otherBuckets = buckets.filter(b => b.id !== bucketId);

  // Range for the vertical repetition picker (1 to 30)
  const repetitionRange = Array.from({ length: 30 }, (_, i) => i + 1);

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
        <div className="w-full max-w-lg bg-[#1e293b] rounded-2xl shadow-2xl border border-white/10 p-5 space-y-4 animate-in zoom-in-95 duration-200 overflow-y-auto max-h-[85vh] custom-scrollbar">
            <div className="flex justify-between items-center border-b border-white/10 pb-3">
                <h3 className="text-lg font-bold text-white">{mode === 'create' ? t.createTask : t.editTask}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-white">
                    <X size={20} />
                </button>
            </div>

            <div>
                <label className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1 block">{t.taskName}</label>
                <input 
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t.pourSomething}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all shadow-inner"
                    autoFocus
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2 block">{t.difficulty}</label>
                    <div className="flex flex-col gap-1.5">
                        {['easy', 'medium', 'hard'].map((level) => (
                            <button
                                key={level}
                                onClick={() => setDifficulty(level as any)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                                    difficulty === level 
                                    ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-600/20' 
                                    : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'
                                }`}
                            >
                                <div className={`w-2 h-2 rounded-full ${level === 'easy' ? 'bg-green-400' : level === 'medium' ? 'bg-yellow-400' : 'bg-red-400'}`}></div>
                                {level === 'easy' ? t.diffEasy : level === 'medium' ? t.diffMedium : t.diffHard}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2 block">{t.timeConsumption}</label>
                    <div className="flex flex-col gap-1.5">
                        {['short', 'medium', 'long'].map((time) => (
                            <button
                                key={time}
                                onClick={() => setTimeConsumption(time as any)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                                    timeConsumption === time 
                                    ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-600/20' 
                                    : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'
                                }`}
                            >
                                <Watch size={12} className={time === 'short' ? 'text-green-400' : time === 'medium' ? 'text-yellow-400' : 'text-red-400'} />
                                {time === 'short' ? t.timeShort : time === 'medium' ? t.timeMedium : t.timeLong}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between bg-white/5 rounded-xl p-3 border border-white/5">
                <span className="text-xs text-slate-400 uppercase tracking-wider font-bold">{t.calculatedPoints}</span>
                <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-white">
                        {calculatePoints(difficulty, timeConsumption)}
                    </span>
                    <span className="text-xs text-indigo-400 font-bold">{t.pts}</span>
                </div>
            </div>

            <div className={`grid ${recurrence ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'} gap-4 pt-2`}>
                <div>
                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1 block">{t.repeat}</label>
                    <button
                        onClick={() => {
                            const next = !recurrence ? 'daily' : recurrence === 'daily' ? 'weekly' : recurrence === 'weekly' ? 'monthly' : undefined;
                            setRecurrence(next);
                        }}
                        className={`w-full py-3 px-3 border border-white/10 rounded-lg flex items-center justify-center gap-2 transition-all min-h-[46px] ${recurrence ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50' : 'bg-slate-900 text-slate-400'}`}
                    >
                        {!recurrence && <Repeat size={14} />}
                        {recurrence === 'daily' && <CalendarClock size={14} />}
                        {recurrence === 'weekly' && <CalendarDays size={14} />}
                        {recurrence === 'monthly' && <Calendar size={14} />}
                        <span className="text-xs capitalize">{recurrence ? getRecurrenceLabel(recurrence) : t.noRepeat}</span>
                    </button>
                </div>

                {recurrence && (
                    <div className="flex flex-col">
                        <label className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1 block">{t.repetitions}</label>
                        <div 
                            ref={scrollRef}
                            className="relative flex flex-col h-[100px] bg-slate-900 border border-slate-700 rounded-xl overflow-y-auto scroll-smooth custom-scrollbar-hide snap-y snap-mandatory"
                        >
                            <div className="py-[30px]"> {/* Vertical padding to center first/last items */}
                                {repetitionRange.map((num) => (
                                    <button
                                        key={num}
                                        data-value={num}
                                        onClick={() => setTargetCount(num)}
                                        className={`w-full h-[40px] flex items-center justify-center text-sm font-bold transition-all snap-center shrink-0 ${
                                            targetCount === num 
                                            ? 'bg-indigo-600 text-white scale-110 shadow-lg z-10' 
                                            : 'text-slate-500 hover:text-slate-300'
                                        }`}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className={recurrence ? '' : 'col-span-1'}>
                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1 block">{t.scheduledDate}</label>
                    <div className="relative flex items-center h-[46px]">
                        <input 
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                            onClick={(e) => {
                                try { (e.target as any).showPicker(); } catch (err) {}
                            }}
                            className="w-full h-full px-3 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-indigo-500 transition-all [color-scheme:dark] cursor-pointer"
                        />
                    </div>
                </div>
            </div>

            {otherBuckets.length > 0 && (
                <div className="pt-2">
                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2 block">{t.alsoCountsFor}</label>
                    <div className="max-h-24 overflow-y-auto space-y-1 pr-1 custom-scrollbar bg-slate-900/50 p-2 rounded-xl border border-slate-700">
                        {otherBuckets.map(b => (
                            <label key={b.id} className="flex items-center gap-2 p-1.5 hover:bg-white/5 rounded-lg cursor-pointer transition-colors">
                                <input 
                                type="checkbox"
                                checked={contributesTo.includes(b.id)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setContributesTo(prev => [...prev, b.id]);
                                    } else {
                                        setContributesTo(prev => prev.filter(id => id !== b.id));
                                    }
                                }}
                                className="rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500 w-4 h-4"
                                />
                                <span className="text-xs text-slate-300">{b.name}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <button 
                onClick={handleSave}
                disabled={!text.trim()}
                className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed mt-2 active:scale-95 transition-all"
            >
                {mode === 'create' ? t.addTask : t.saveTask}
            </button>
        </div>
    </div>
  );
};
